import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { test as fuzzy } from 'fuzzy';

import { Flex, Box } from 'rebass';

import { GlowHeading, SelectCabinInformation, SelectCabinAllDone, SelectCabinAllCabinsNotPopulated, Filter } from 'components';

import { Modal, SelectGuestListEntry, Cabin, T, FilterCheckbox, FilterDropdown, FilterText } from 'containers';

import { ADULT, CHILD, SENIOR, STUDENT } from 'domain/age';
import { AppText } from 'domain/appText';

import './styles.css';

const Wrapper = styled.div`
  margin: 0 -1rem;
  min-width: 950px;
  width: 100%;
  height: 75vh;
`;
const List = styled.ul`
  margin: 0;
  padding: 0;
  padding-top: 1px;
`;
const ScrollBox = styled(Box)`
  height: 100%;
  overflow-y: auto;
  position: absolute;
  top: 0;
  bottom: 0;
`;
const StyledPassengerBox = styled(ScrollBox)`
  border-left: 3px solid #dae6f2;
  right: 0;
  left: auto;
`;
const Content = styled(Flex)`
  position: absolute;
  top: 0;
  bottom: 20px;
  left: 0;
  width: 100%;
`;
const Footer = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #dae6f2;
  height: 20px;
`;
const StyledGlowHeading = styled(GlowHeading)`
  font-size: 1em;
  line-height: 1em;
  padding: 20px;
  margin: 0;
`;

const isCategoryIncludedInFilter = (category, filter) => {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'adults') {
    return category === ADULT || category === SENIOR || category === STUDENT;
  }
  if (filter === 'children') {
    return category === CHILD;
  }
  return true;
};

class SelectCabinModal extends Component {
  static propTypes = {
    departureId: PropTypes.string.isRequired,
    routeName: PropTypes.string,
    cabins: PropTypes.array,
    guests: PropTypes.array,
    onClose: PropTypes.func,
    activeCabin: PropTypes.string,
    isAllGuestsInCabin: PropTypes.bool,
    isCabinFull: PropTypes.array,
    mergeCabinsWithIsCabinFull: PropTypes.array,
    isAllCabinsPopulated: PropTypes.bool,
    cabinsAllocated: PropTypes.bool,
    setActiveCabin: PropTypes.func,
  }
  static defaultProps = {
    cabins: [],
    guests: [],
    onClose: () => {},
    isCabinFull: [],
  }

  constructor() {
    super();
    this.state = {
      tabActive: 0,
    };
  }

  toggleTab = (tab, cabins, isCabinFull) => {
    const { tabActive } = this.state;
    this.setState({ tabActive: tab });
    if (tabActive === 0) {
      this.props.setActiveCabin('');
    } else if (tabActive === 1) {
      const getFirstCabinThatIsEmpty = cabins.filter((id, i) => isCabinFull[i] ? null : id);
      if (getFirstCabinThatIsEmpty.length) {
        this.props.setActiveCabin(getFirstCabinThatIsEmpty[0]);
      }
    }
  }

  renderListOfAvailableCabins = (cabins, isCabinFull, departureId, cabinsAllocated) => cabins.map((id, i) => (
    isCabinFull[i] ?
    null
    : <Cabin
      key={id}
      id={id}
      departureId={departureId}
      cabinsAllocated={cabinsAllocated}
    />
  ));

  renderListOfFullCabins = (cabins, isCabinFull, departureId, cabinsAllocated) => cabins.map((id, i) => (
    isCabinFull[i] ?
      <Cabin
        key={id}
        id={id}
        departureId={departureId}
        cabinsAllocated={cabinsAllocated}
      />
    : null
  ));

  render() {
    const {
      departureId,
      routeName,
      cabins,
      guests,
      activeCabin,
      isAllGuestsInCabin,
      isCabinFull,
      isAllCabinsPopulated,
      cabinsAllocated,
    } = this.props;
    const { tabActive } = this.state;
    const lengthOfFullCabinTab = isCabinFull.filter(el => el === true);
    const lengthOfAvailableCabinTab = isCabinFull.filter(el => el === false);
    const hasGuests = guests && guests.length !== 0;

    let passengerContent;
    if (isAllGuestsInCabin) {
      if (!isAllCabinsPopulated) {
        passengerContent = <SelectCabinAllCabinsNotPopulated />;
      } else {
        passengerContent = <SelectCabinAllDone />;
      }
    } else if (!activeCabin) {
      passengerContent = <SelectCabinInformation />;
    } else if (hasGuests) {
      passengerContent = (
        <T
          text={[
            AppText.FilterNoCabinTitle,
            AppText.FilterNoCabinDescription,
            AppText.SearchGuestPlaceholder,
            AppText.Adults,
            AppText.Children,
            AppText.All,
          ]}
          render={([
            filterNoCabinTitle,
            filterNoCabinDescription,
            searchGuestPlaceholder,
            adult,
            children,
            all,
          ]) => (
            <Filter
              filter={`passenger-filter-${departureId}`}
              departureId={departureId}
              middlewares={[{
                name: 'nocabin',
                group: 'hide',
                defaultValue: true,
                component: <FilterCheckbox label={filterNoCabinTitle} title={filterNoCabinDescription} />,
                rule: (value, { selectedCabin }) => value ? !selectedCabin || !selectedCabin[departureId] : true,
              }, {
                name: 'categories',
                group: 'categories',
                defaultValue: 'all',
                component: (
                  <FilterDropdown
                    options={[
                      { code: 'all', text: `${all}` },
                      { code: 'adults', text: `${adult}` },
                      { code: 'children', text: `${children}` },
                    ]}
                    defaultValue="all"
                    arrowColor="#000"
                  />
                ),
                rule: (value, { customerCategory }) => isCategoryIncludedInFilter(customerCategory, value),
              }, {
                name: 'name',
                group: 'search',
                component: <FilterText placeholder={searchGuestPlaceholder} />,
                rule: (value, { firstName, lastName }) => value ? fuzzy(value, `${firstName} ${lastName}`) : true,
              }]}
              items={guests}
              renderItem={(id, i, filterStatus) => (
                <SelectGuestListEntry
                  key={id}
                  id={id}
                  number={i + 1}
                  departureId={departureId}
                  filterStatus={filterStatus}
                  cabinsAllocated={cabinsAllocated}
                />
              )}
            />
          )}
        />
      );
    } else {
      passengerContent = (
        <T text={AppText.ManageCabinsNoCabinSelectedText} render={text => <span>{text}</span>} />
      );
    }

    return (
      <T
        text={[
          AppText.ManageCabinsFor,
          AppText.ManageCabinsListOfGuests,
          AppText.FilterTabAvailableCabins,
          AppText.FilterTabFullCabins,
        ]}
        render={([
          manageCabinsFor,
          listOfGuests,
          filterTabAvailableCabins,
          filterTabFullCabins,
        ]) => (
          <Modal title={`${manageCabinsFor} ${routeName}`} name={`selectCabin-${departureId}`} closeable>
            <Wrapper>
              <Content className={cabinsAllocated ? 'allocated-cabins' : ''}>
                <ScrollBox w={6.5 / 10}>
                  <div className="cabin-tab-container">
                    <div role="button" tabIndex={0} onClick={() => this.toggleTab(0, cabins, isCabinFull)} className={`cabin-tab${tabActive === 0 ? ' active' : ''}`}>
                      <a>{filterTabAvailableCabins}</a><span>{lengthOfAvailableCabinTab.length}</span>
                    </div>
                    <div role="button" tabIndex={0} onClick={() => this.toggleTab(1, cabins, isCabinFull)} className={`cabin-tab${tabActive === 1 ? ' active' : ''}`}>
                      <a>{filterTabFullCabins}</a><span>{lengthOfFullCabinTab.length}</span>
                    </div>
                  </div>
                  <List>
                    {tabActive === 0 ? this.renderListOfAvailableCabins(cabins, isCabinFull, departureId, cabinsAllocated) : this.renderListOfFullCabins(cabins, isCabinFull, departureId, cabinsAllocated)}
                  </List>
                </ScrollBox>
                <StyledPassengerBox w={3.5 / 10}>
                  <div>
                    {(activeCabin && !isAllGuestsInCabin) && <StyledGlowHeading level={5}>{listOfGuests}</StyledGlowHeading>}
                    {passengerContent}
                  </div>
                </StyledPassengerBox>
              </Content>
              <Footer py={2} px={4} />
            </Wrapper>
          </Modal>
        )}
      />
    );
  }
}

export default SelectCabinModal;



// WEBPACK FOOTER //
// ./src/components/organisms/SelectCabinModal/index.js